import type { IDockviewPanelProps } from 'dockview-core';
import type { FunctionComponent } from 'react';
import type { PanelId } from '@trajectory/ui';
import { PanelErrorBoundary } from '@/app/PanelErrorBoundary';
import { AnalyticsPanel } from './AnalyticsPanel';
import { ChartsPanel } from './ChartsPanel';
import { InspectorPanel } from './InspectorPanel';
import { Map2DPanel } from './Map2DPanel';
import { MissionExplorerPanel } from './MissionExplorerPanel';
import { OptimizerPanel } from './OptimizerPanel';
import { OrbitEditorPanel } from './OrbitEditorPanel';
import { RocketEditorPanel } from './RocketEditorPanel';
import { TimelinePanel } from './TimelinePanel';
import { TransferPlannerPanel } from './TransferPlannerPanel';
import { Viewport3DPanel } from './Viewport3DPanel';

function withPanelBoundary(
  panelId: PanelId,
  Panel: FunctionComponent<IDockviewPanelProps>,
): FunctionComponent<IDockviewPanelProps> {
  const BoundedPanel = (props: IDockviewPanelProps) => (
    <PanelErrorBoundary panelId={panelId}>
      <Panel {...props} />
    </PanelErrorBoundary>
  );
  BoundedPanel.displayName = `Bounded${Panel.displayName ?? Panel.name ?? panelId}`;
  return BoundedPanel;
}

const RAW_PANEL_COMPONENTS: Record<
  PanelId,
  FunctionComponent<IDockviewPanelProps>
> = {
  'mission-explorer': MissionExplorerPanel,
  'rocket-editor': RocketEditorPanel,
  'orbit-editor': OrbitEditorPanel,
  'viewport-3d': Viewport3DPanel,
  'map-2d': Map2DPanel,
  charts: ChartsPanel,
  timeline: TimelinePanel,
  inspector: InspectorPanel,
  analytics: AnalyticsPanel,
  optimizer: OptimizerPanel,
  'transfer-planner': TransferPlannerPanel,
};

/** Maps every dock panel through its own isolated recovery boundary. */
export const PANEL_COMPONENTS = Object.fromEntries(
  (Object.entries(RAW_PANEL_COMPONENTS) as [
    PanelId,
    FunctionComponent<IDockviewPanelProps>,
  ][]).map(([id, Panel]) => [id, withPanelBoundary(id, Panel)]),
) as Record<PanelId, FunctionComponent<IDockviewPanelProps>>;
